import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { metalPriceService, MetalPricesData } from '../services/metalPriceService';

interface MetalPriceContextType {
    metalData: MetalPricesData | null;
    loading: boolean;
    refreshPrices: (forceRefresh?: boolean) => Promise<void>;
}

const MetalPriceContext = createContext<MetalPriceContextType | undefined>(undefined);

export const MetalPriceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [metalData, setMetalData] = useState<MetalPricesData | null>(null);
    const [loading, setLoading] = useState(true);

    const loadPrices = async (forceRefresh: boolean = false) => {
        try {
            if (!forceRefresh) setLoading(true);
            const data = await metalPriceService.getMetalPrices(forceRefresh);
            setMetalData(data);
        } catch (error) {
            console.error('Error loading metal prices in context:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPrices();
    }, []);

    const refreshPrices = async (forceRefresh: boolean = true) => {
        await loadPrices(forceRefresh);
    };

    return (
        <MetalPriceContext.Provider value={{ metalData, loading, refreshPrices }}>
            {children}
        </MetalPriceContext.Provider>
    );
};

export const useMetalPrices = () => {
    const context = useContext(MetalPriceContext);
    if (!context) {
        throw new Error('useMetalPrices must be used within a MetalPriceProvider');
    }
    return context;
};
